"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DepositRefund = void 0;
const bitcoin_1 = require("../../lib/bitcoin");
const contracts_1 = require("../../lib/contracts");
const utils_1 = require("../../lib/utils");
const bitcoinjs_lib_1 = require("bitcoinjs-lib");
/**
 * Component allowing to craft and submit the Bitcoin refund transaction using
 * the given tBTC v2 deposit script.
 *
 * @experimental THIS IS EXPERIMENTAL CODE THAT CAN BE CHANGED OR REMOVED
 *               IN FUTURE RELEASES. IT SHOULD BE USED ONLY FOR INTERNAL
 *               PURPOSES AND EXTERNAL APPLICATIONS SHOULD NOT DEPEND ON IT.
 */
// TODO: Abstract away transaction signing so there is no need to deal with
//       private key directly.
class DepositRefund {
    constructor(script) {
        this.script = script;
    }
    static fromScript(script) {
        return new DepositRefund(script);
    }
    /**
     * Submits a deposit refund by creating and broadcasting a Bitcoin P2(W)PKH
     * deposit refund transaction.
     * @param bitcoinClient - Bitcoin client used to interact with the network.
     * @param fee - the value that will be subtracted from the deposit UTXO being
     *        refunded and used as the transaction fee.
     * @param utxo - UTXO that was created during depositing that needs be refunded.
     * @param refunderAddress - Recipient Bitcoin wallet address of the refunded
     *        deposit.
     * @param refunderPrivateKey - Bitcoin wallet private key of the refunder.
     *        It must correspond to the `refundPublicKeyHash` of the deposit script.
     * @returns The outcome is the deposit refund transaction hash.
     * @dev This function should be called by the refunder after `refundLocktime`
     *      passes plus 1 hour. The additional hour of waiting is the result of
     *      adopting BIP113 which compares the transaction's locktime against the
     *      median timestamp of the last 11 blocks. This median time lags
     *      the current unix time by about 1 hour.
     */
    async submitTransaction(bitcoinClient, fee, utxo, refunderAddress, refunderPrivateKey) {
        const utxoRawTransaction = await bitcoinClient.getRawTransaction(utxo.transactionHash);
        const utxoWithRaw = Object.assign(Object.assign({}, utxo), { transactionHex: utxoRawTransaction.transactionHex });
        const bitcoinNetwork = await bitcoinClient.getNetwork();
        const { transactionHash, rawTransaction } = await this.assembleTransaction(bitcoinNetwork, fee, utxoWithRaw, refunderAddress, refunderPrivateKey);
        // Note that `broadcast` may fail silently (i.e. no error will be returned,
        // even if the transaction is rejected by other nodes and does not enter the
        // mempool, for example due to an UTXO being already spent).
        await bitcoinClient.broadcast(rawTransaction);
        return { transactionHash };
    }
    /**
     * Assembles a Bitcoin P2(W)PKH deposit refund transaction.
     * @param bitcoinNetwork - The target Bitcoin network.
     * @param fee - the value that will be subtracted from the deposit UTXO being
     *        refunded and used as the transaction fee.
     * @param utxo - UTXO that was created during depositing that needs be refunded.
     * @param refunderAddress - Recipient Bitcoin wallet address of the refunded
     *        deposit.
     * @param refunderPrivateKey - Bitcoin wallet private key of the refunder.
     *        It must correspond to the `refundPublicKeyHash` of the deposit script.
     * @returns The outcome consisting of:
     *          - the deposit refund transaction hash,
     *          - the refund transaction in the raw format.
     */
    async assembleTransaction(bitcoinNetwork, fee, utxo, refunderAddress, refunderPrivateKey) {
        (0, contracts_1.validateDepositReceipt)(this.script.receipt);
        const refunderKeyPair = bitcoin_1.BitcoinPrivateKeyUtils.createKeyPair(refunderPrivateKey, bitcoinNetwork);
        const outputValue = utxo.value.sub(fee);
        const transaction = new bitcoinjs_lib_1.Transaction();
        transaction.addInput(utxo.transactionHash.reverse().toBuffer(), utxo.outputIndex);
        const outputScript = bitcoin_1.BitcoinAddressConverter.addressToOutputScript(refunderAddress, bitcoinNetwork);
        transaction.addOutput(outputScript.toBuffer(), outputValue.toNumber());
        // In order to be able to spend the UTXO being refunded the transaction's
        // locktime must be set to a value equal to or higher than the refund locktime.
        // Additionally, the input's sequence must be set to a value different than
        // `0xffffffff`. These requirements are the result of BIP-65.
        transaction.locktime = locktimeToUnixTimestamp(this.script.receipt.refundLocktime);
        transaction.ins[0].sequence = 0xfffffffe;
        // Sign the input
        const previousOutput = bitcoinjs_lib_1.Transaction.fromHex(utxo.transactionHex).outs[utxo.outputIndex];
        const previousOutputValue = previousOutput.value;
        const previousOutputScript = utils_1.Hex.from(previousOutput.script);
        if (bitcoin_1.BitcoinScriptUtils.isP2SHScript(previousOutputScript)) {
            // P2SH deposit UTXO
            await this.signP2SHDepositInput(transaction, 0, refunderKeyPair);
        }
        else if (bitcoin_1.BitcoinScriptUtils.isP2WSHScript(previousOutputScript)) {
            // P2WSH deposit UTXO
            await this.signP2WSHDepositInput(transaction, 0, previousOutputValue, refunderKeyPair);
        }
        else {
            throw new Error("Unsupported UTXO script type");
        }
        const transactionHash = bitcoin_1.BitcoinTxHash.from(transaction.getId());
        return {
            transactionHash,
            rawTransaction: {
                transactionHex: transaction.toHex(),
            },
        };
    }
    /**
     * Assembles the deposit script based on the given deposit details. Performs
     * validations on values and key formats.
     * @param refunderKeyPair - Signer object containing the refunder's key pair.
     * @returns A Promise resolving to the assembled deposit script as a Buffer.
     * @throws Error if there are discrepancies in values or key formats.
     */
    async prepareDepositScript(refunderKeyPair) {
        const refunderPublicKey = utils_1.Hex.from(refunderKeyPair.publicKey);
        if (!bitcoin_1.BitcoinHashUtils.computeHash160(refunderPublicKey).equals(this.script.receipt.refundPublicKeyHash)) {
            throw new Error("Refund public key does not correspond to wallet private key");
        }
        if (!bitcoin_1.BitcoinPublicKeyUtils.isCompressedPublicKey(refunderPublicKey)) {
            throw new Error("Refunder public key must be compressed");
        }
        return (await this.script.getPlainText()).toBuffer();
    }
    /**
     * Signs a P2SH deposit transaction input and sets the `scriptSig`.
     * @param transaction - The transaction containing the input to be signed.
     * @param inputIndex - Index pointing to the input within the transaction.
     * @param refunderKeyPair - A Signer object with the refunder's public and private
     *        key pair.
     * @returns An empty promise upon successful signing.
     */
    async signP2SHDepositInput(transaction, inputIndex, refunderKeyPair) {
        const depositScript = await this.prepareDepositScript(refunderKeyPair);
        const sigHashType = bitcoinjs_lib_1.Transaction.SIGHASH_ALL;
        const sigHash = transaction.hashForSignature(inputIndex, depositScript, sigHashType);
        const signature = bitcoinjs_lib_1.script.signature.encode(refunderKeyPair.sign(sigHash), sigHashType);
        const scriptSig = [];
        scriptSig.push(signature);
        scriptSig.push(refunderKeyPair.publicKey);
        scriptSig.push(depositScript);
        transaction.ins[inputIndex].script = bitcoinjs_lib_1.script.compile(scriptSig);
    }
    /**
     * Signs a P2WSH deposit transaction input and sets the witness script.
     * @param transaction - The transaction containing the input to be signed.
     * @param inputIndex - Index pointing to the input within the transaction.
     * @param previousOutputValue - The value from the previous transaction output.
     * @param refunderKeyPair - A Signer object with the refunder's public and private
     *        key pair.
     * @returns An empty promise upon successful signing.
     */
    async signP2WSHDepositInput(transaction, inputIndex, previousOutputValue, refunderKeyPair) {
        const depositScript = await this.prepareDepositScript(refunderKeyPair);
        const sigHashType = bitcoinjs_lib_1.Transaction.SIGHASH_ALL;
        const sigHash = transaction.hashForWitnessV0(inputIndex, depositScript, previousOutputValue, sigHashType);
        const signature = bitcoinjs_lib_1.script.signature.encode(refunderKeyPair.sign(sigHash), sigHashType);
        const witness = [];
        witness.push(signature);
        witness.push(refunderKeyPair.publicKey);
        witness.push(depositScript);
        transaction.ins[inputIndex].witness = witness;
    }
}
exports.DepositRefund = DepositRefund;
/**
 * Converts locktime from the little endian hexstring format to the Unix
 * timestamp.
 * @param locktime - Locktime as a little endian hexstring.
 * @returns Locktime as a Unix timestamp.
 */
function locktimeToUnixTimestamp(locktime) {
    const bigEndianLocktime = locktime.reverse().toString();
    return parseInt(bigEndianLocktime, 16);
}
//# sourceMappingURL=refund.js.map